import { InjectionToken } from '@angular/core';
import { Observable } from 'rxjs';
import { TwoFactorAuthenticationMethod } from './enums';

export interface VerificationFlowResult {
  shouldSave: boolean;
}

export interface VerificationCodeRequestResponse {
  method: TwoFactorAuthenticationMethod;
  requestAgainAt: Date | null;
  expiresAt: Date | null;
}

export type VerifyCodeResponse = {
  succeeded: false;
  hasMoreAttempts: boolean;
  requiresNewCode: boolean;
  tryAgainAt: string | null;
} | {
  succeeded: true;
  hasMoreAttempts: false;
  token: string;
};

export interface ITwoFactorVerificationService {
  /** Gets the email verification token that can be attached to requests to prove verification to the server. */
  get verificationToken(): string | null;

  /** When the specified authentication method expires and would need re-requesting. */
  verificationCodeExpiresAt(method: TwoFactorAuthenticationMethod): Date | null;

  /** How long the user must wait before being able to re-request a code for the specified authentication method. */
  canRequestVerificationCodeAt(method: TwoFactorAuthenticationMethod): Date | null;

  /** At what time the user must wait before being able to make another verification attempt for the specified authentication method. */
  canAttemptVerificationAt(method: TwoFactorAuthenticationMethod): Date | null;

  /** Whether the user must make another verification code request before being able to make additional verification attempts. */
  requiresNewVerificationCode(method: TwoFactorAuthenticationMethod): boolean;

  /** If the user is not currently verified, starts a verification flow as per `beginVerificationFlow`. */
  assertVerified(): Promise<void>;

  /** Starts a two-factor verification flow (showing modal asking user to verify their identity).
   * If the verification was successful, the returned promise resolves. If the verification failed or was cancel, it
   * instead rejects. */
  beginVerificationFlow(): Promise<void>;

  /**
   * Begins a request for a verification code.
   * @param method If not provided, the server determines the default authentication method to use (which is returned in the response).
   * If provided, instructs the server to specifically use this method instead (e.g. user does not have access to app-based).
   */
  requestVerificationCode(method?: TwoFactorAuthenticationMethod): Observable<VerificationCodeRequestResponse>;

  submitVerificationCode(code: string, method: TwoFactorAuthenticationMethod): Observable<VerifyCodeResponse>;

  /** Saves the currently stored verification token for future sessions to use. */
  persistVerificationToken(): void;

  /** Sets a new verification token. */
  setVerificationToken(token: string, rememberMe?: boolean): void;

  /** Removes a persisted verification token, requiring the user to re-verify their identity. */
  unsetVerificationToken(): void;
}

export const TWO_FACTOR_VERIFICATION_SERVICE = new InjectionToken<ITwoFactorVerificationService>('twoFactorVerificationService');
